import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface EmailStats {
  totalProcessed: number;
  thisWeek: number;
  lastEmailTime: string | null;
  recentEmails: any[];
}

export const useGmailStats = (customerId: string) => {
  const [emailStats, setEmailStats] = useState<EmailStats>({
    totalProcessed: 0,
    thisWeek: 0,
    lastEmailTime: null,
    recentEmails: []
  });
  const [loading, setLoading] = useState(true);

  const fetchEmailStats = async () => {
    if (!customerId) return;
    
    setLoading(true);
    try {
      // Get customer information first
      const { data: customer } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customerId)
        .single();

      if (!customer) return;

      // Search for ingestion events using customer name pattern
      const customerToken = customer.name.toLowerCase().replace(/\s+/g, '');
      
      // Get total processed emails for this customer from ingestion_events
      const { data: totalData } = await supabase
        .from('ingestion_events')
        .select('*')
        .eq('user_id', customerId)
        .in('source_type', ['gmail', 'mailbox'])
        .in('event_type', ['email_processed', 'message_processed'])
        .eq('status', 'success');

      // Also check bill_upload_queue for Gmail source
      const { data: queueData } = await supabase
        .from('bill_upload_queue')
        .select('*')
        .eq('user_id', customerId)
        .eq('source_type', 'gmail')
        .order('created_at', { ascending: false });

      // Get emails from this week
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const thisWeekIngestion = totalData?.filter(item => 
        new Date(item.created_at) > weekAgo
      ) || [];
      const thisWeekQueue = queueData?.filter(item => 
        new Date(item.created_at) > weekAgo
      ) || [];

      // Combine both sources for total count
      const totalProcessed = (totalData?.length || 0) + (queueData?.length || 0);
      const thisWeekTotal = thisWeekIngestion.length + thisWeekQueue.length;

      // Get most recent activity from either source
      const allRecent = [
        ...(totalData || []).map(item => ({ ...item, source: 'ingestion' })),
        ...(queueData || []).map(item => ({ ...item, source: 'queue' }))
      ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setEmailStats({
        totalProcessed,
        thisWeek: thisWeekTotal,
        lastEmailTime: allRecent[0]?.created_at || null,
        recentEmails: allRecent.slice(0, 5)
      });
    } catch (error) {
      console.error('Failed to fetch email stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailStats();
  }, [customerId]);

  return {
    emailStats,
    loading,
    refreshStats: fetchEmailStats
  };
};

// Utility function for time formatting
export const formatTimeAgo = (timestamp: string) => {
  const now = new Date();
  const time = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - time.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  return `${Math.floor(diffInSeconds / 86400)} days ago`;
};